import React from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save, Maximize2, Minimize2 } from "lucide-react";
import { Tldraw, defaultShapeUtils } from "tldraw";
import { createTLStore, getSnapshot, loadSnapshot } from "@tldraw/editor";
import "tldraw/tldraw.css";

/**
 * tldraw-backed whiteboard.
 *  - Handles text editing, resize, freehand pressure (tablet + touch), and
 *    fullscreen properly out-of-the-box.
 *  - Persists tldraw's document snapshot to `PUT /guidance/{id}/whiteboard`
 *    under the `scene` field (backward-compatible with the old `strokes`).
 *  - Real-time peers share diffs through the existing workspace WebSocket
 *    (`wb_scene` event) — we broadcast the current snapshot on user edits,
 *    throttled to ~6/sec.
 */
export function WhiteboardTab({ guidanceId, wsRef, currentUser }) {
  const [store] = React.useState(() => createTLStore({ shapeUtils: defaultShapeUtils }));
  const [loaded, setLoaded] = React.useState(false);
  const [fullscreen, setFullscreen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [meta, setMeta] = React.useState({ updated_at: null, updated_by_name: null });
  const suppressBroadcast = React.useRef(false);
  const throttleRef = React.useRef({ last: 0 });

  // ---- Load persisted snapshot ----
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await api.get(`/guidance/${guidanceId}/whiteboard`);
        if (!mounted) return;
        const snap = r.data?.scene?.snapshot;
        if (snap && snap.document) {
          suppressBroadcast.current = true;
          try { loadSnapshot(store, snap); }
          finally { setTimeout(() => { suppressBroadcast.current = false; }, 0); }
        }
        setMeta({ updated_at: r.data?.updated_at, updated_by_name: r.data?.updated_by_name });
      } catch { /* silent */ }
      finally { if (mounted) setLoaded(true); }
    })();
    return () => { mounted = false; };
  }, [guidanceId, store]);

  // ---- Broadcast local changes to peers + trigger debounced auto-save ----
  const autoSaveTimerRef = React.useRef(null);
  const scheduleAutoSave = React.useCallback(() => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      // Persist the current snapshot to REST so a page refresh (or another
      // collaborator joining later) picks up the exact same board.
      try {
        const snapshot = getSnapshot(store);
        api.put(`/guidance/${guidanceId}/whiteboard`, { strokes: [], scene: { snapshot } })
          .then((r) => setMeta({ updated_at: r.data?.updated_at, updated_by_name: r.data?.updated_by_name }))
          .catch(() => { /* auto-save is best-effort; the Save button still works */ });
      } catch { /* noop */ }
    }, 1200);
  }, [store, guidanceId]);

  React.useEffect(() => {
    const unsub = store.listen(
      () => {
        scheduleAutoSave();
        if (suppressBroadcast.current) return;
        const now = Date.now();
        if (now - throttleRef.current.last < 180) return;
        throttleRef.current.last = now;
        const ws = wsRef?.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        try {
          const snap = getSnapshot(store);
          ws.send(JSON.stringify({ type: "wb_scene", data: { snapshot: snap, user_id: currentUser?.user_id } }));
        } catch { /* noop */ }
      },
      { source: "user", scope: "document" },
    );
    return () => unsub();
  }, [store, wsRef, currentUser?.user_id, scheduleAutoSave]);

  // ---- Apply peer changes ----
  React.useEffect(() => {
    const ws = wsRef?.current;
    if (!ws) return;
    const handler = (e) => {
      let payload; try { payload = JSON.parse(e.data); } catch { return; }
      const t = payload?.type, d = payload?.data;
      if (t !== "wb_scene" || !d || d.user_id === currentUser?.user_id) return;
      if (!d.snapshot) return;
      suppressBroadcast.current = true;
      try { loadSnapshot(store, d.snapshot); }
      finally { setTimeout(() => { suppressBroadcast.current = false; }, 0); }
    };
    ws.addEventListener("message", handler);
    return () => ws.removeEventListener("message", handler);
  }, [store, wsRef, currentUser?.user_id]);

  const save = async () => {
    setBusy(true);
    try {
      const snapshot = getSnapshot(store);
      const r = await api.put(`/guidance/${guidanceId}/whiteboard`, {
        strokes: [],
        scene: { snapshot },
      });
      setMeta({ updated_at: r.data?.updated_at, updated_by_name: r.data?.updated_by_name });
      toast.success("Whiteboard saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const containerRef = React.useRef(null);
  const boardHostRef = React.useRef(null);

  // Fullscreen: use the native Fullscreen API on the tldraw host div. Native
  // fullscreen doesn't touch our parent flexbox layout (so it can't flicker
  // as a sibling collapses/expands) and it grows the same DOM node so
  // tldraw's ResizeObserver picks up the new size cleanly — no re-mount,
  // no re-scale of a fixed-size canvas.
  React.useEffect(() => {
    const onChange = () => {
      const active = document.fullscreenElement === boardHostRef.current;
      setFullscreen(active);
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = React.useCallback(async () => {
    const el = boardHostRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement === el) {
        await document.exitFullscreen();
      } else if (el.requestFullscreen) {
        await el.requestFullscreen();
      }
    } catch {
      // Fallback for browsers/environments where the Fullscreen API is
      // blocked (e.g. some iframe policies): flip a state flag that the
      // render layer uses to promote the host to a top-layer overlay.
      setFullscreen((v) => !v);
    }
  }, []);

  return (
    <div ref={containerRef} className="kb-card p-3 space-y-2" data-testid="workspace-whiteboard-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
          Whiteboard {meta.updated_at && <span>· saved by {meta.updated_by_name} at {new Date(meta.updated_at).toLocaleString()}</span>}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={save}
            disabled={busy || !loaded}
            className="kb-btn-primary text-xs inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="whiteboard-save"
          >
            <Save className="w-3 h-3" /> {busy ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            onClick={toggleFullscreen}
            className="p-1.5 border border-slate-200 rounded-sm text-kb-ink-muted hover:bg-slate-100"
            title={fullscreen ? "Exit fullscreen (Esc)" : "Fullscreen"}
            data-testid="whiteboard-fullscreen"
          >
            {fullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
      <div
        ref={boardHostRef}
        className={`w-full bg-white ${fullscreen ? "kb-wb-fs" : "border border-slate-200 rounded-sm overflow-hidden"}`}
        style={fullscreen ? undefined : { height: "72vh" }}
      >
        {loaded && <Tldraw store={store} />}
      </div>
    </div>
  );
}

export default WhiteboardTab;
